/**
 * Client-side UTM campaign attribution.
 *
 * Captures who brought a visitor in — source, campaign, term — from, in
 * priority order:
 *   1. A plain query string: ?utm_source=&utm_campaign=&utm_term=
 *   2. A vanity link: /{source}/{campaignSlug}/{term}
 *      e.g. /instagram/octfest/oct20
 *   3. The browser's Referer header, when neither of the above is present —
 *      lets a single static link (e.g. just "/") still pick up a source
 *      automatically. See detectSourceFromReferrer()'s own comment for why
 *      this is necessarily best-effort, not complete.
 *
 * source and term are free text, chosen at share time — NOT pre-registered
 * per campaign, so one campaign can be posted across many platforms just by
 * swapping the first URL segment. No utm_medium, no utm_content — this
 * product deliberately doesn't track medium.
 *
 * Attribution is FIRST-TOUCH: whichever campaign link a browser hits first
 * keeps credit for ATTRIBUTION_WINDOW_DAYS, even if the same browser later
 * clicks a different campaign link. That's why this uses localStorage, not
 * sessionStorage (compare journeyTracking.js's evaluationId, which only
 * needs to survive one tab/session) — first-touch has to survive the visitor
 * closing the tab and coming back to buy days later.
 *
 * Same fire-and-forget rule as journeyTracking.js: this must never throw,
 * never block, never break the page. Every call is wrapped accordingly.
 */

const STORAGE_KEY = 'utm_attribution';

// A click today still gets credit for a purchase up to this many days later.
// Single constant — change this to adjust the attribution policy.
const ATTRIBUTION_WINDOW_DAYS = 30;
const ATTRIBUTION_WINDOW_MS = ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000;

// Top-level path segments already claimed by real app routes (see App.js).
// A vanity link's first segment must not collide with these, or a visit to
// e.g. /admin would be mistaken for a UTM source named "admin". Kept in sync
// with backend/routes/campaigns.py's _RESERVED_SLUGS (duplicated
// intentionally — frontend and backend don't share code in this project).
const RESERVED_FIRST_SEGMENTS = new Set([
  'analyze', 'loading', 'results', 'evaluation', 'login', 'admin',
  'recruiter', 'checkout', 'payment-success', 'intake-form',
  'intake-success', 'review', 'delivery', 'terms', 'privacy', 'refund',
  'cancellation', 'shipping', 'about', 'contact', 'pricing', 'feedback',
  'preview',
]);

const sanitize = (v) => (v || '').toString().trim().slice(0, 100).replace(/[^a-zA-Z0-9\-_.]/g, '');

// Known platform domains the browser's Referer header might show. Matched
// against the referrer's hostname (and its subdomains) in order — first
// match wins. Anything external but unrecognized falls back to 'referral'
// rather than being dropped, so it's at least visible as non-direct traffic.
const REFERRER_SOURCE_PATTERNS = [
  { source: 'instagram', hosts: ['instagram.com', 'l.instagram.com'] },
  { source: 'linkedin', hosts: ['linkedin.com', 'lnkd.in'] },
  { source: 'youtube', hosts: ['youtube.com', 'youtu.be'] },
  { source: 'facebook', hosts: ['facebook.com', 'fb.com', 'l.facebook.com', 'lm.facebook.com', 'm.facebook.com'] },
  { source: 'twitter', hosts: ['twitter.com', 'x.com', 't.co'] },
  { source: 'whatsapp', hosts: ['whatsapp.com', 'wa.me', 'api.whatsapp.com'] },
  { source: 'telegram', hosts: ['t.me', 'telegram.org'] },
  { source: 'reddit', hosts: ['reddit.com'] },
  { source: 'tiktok', hosts: ['tiktok.com'] },
  { source: 'pinterest', hosts: ['pinterest.com', 'pin.it'] },
  { source: 'snapchat', hosts: ['snapchat.com'] },
  { source: 'threads', hosts: ['threads.net'] },
  { source: 'google', hosts: ['google.com', 'google.co.in'] },
];

/**
 * Best-effort source detection from document.referrer, for a visitor who
 * arrived via a plain static link (no ?utm_source=, no vanity path) — e.g.
 * one link pasted identically into an Instagram bio, a LinkedIn post, and a
 * YouTube description. Only works when the referring app/browser actually
 * sends a Referer header, which is NOT guaranteed:
 *   - Desktop browser clicks (LinkedIn feed, Twitter/X, Reddit, email
 *     clients that preserve it) generally do send one — detection works.
 *   - Instagram's and LinkedIn's native mobile in-app browsers frequently
 *     strip or blank it — this is platform behavior, not something fixable
 *     from here. Those clicks fall through to null (unattributed), same as
 *     today.
 * This is why per-platform vanity links remain the only ~100%-accurate
 * option; this function is a free, automatic improvement on top of the
 * "no source at all" baseline, not a replacement for it.
 */
const detectSourceFromReferrer = () => {
  try {
    const ref = typeof document !== 'undefined' ? document.referrer : '';
    if (!ref) return null;
    const refHost = new URL(ref).hostname.toLowerCase().replace(/^www\./, '');
    const selfHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
    if (!refHost || refHost === selfHost) return null; // internal navigation, not attribution

    for (const { source, hosts } of REFERRER_SOURCE_PATTERNS) {
      if (hosts.some((h) => refHost === h || refHost.endsWith(`.${h}`))) return source;
    }
    return 'referral'; // known external referrer, unrecognized platform
  } catch (_) {
    return null;
  }
};

/**
 * Parse the current location into a UTM triplet, trying each source in
 * priority order: explicit query params, then a 3-segment vanity path, then
 * (only if neither is present) a best-effort guess from document.referrer.
 * Query params win over a vanity-looking path (an explicit ?utm_source= is a
 * deliberate signal even on a path that also happens to look like a vanity
 * link). Returns null if nothing at all could be determined.
 */
export const parseUtmFromLocation = (pathname, search) => {
  const qs = new URLSearchParams(search || '');
  if (qs.has('utm_source') || qs.has('utm_campaign') || qs.has('utm_term')) {
    let source = sanitize(qs.get('utm_source'));
    const campaign = sanitize(qs.get('utm_campaign'));
    const term = sanitize(qs.get('utm_term'));
    if (!source && !campaign && !term) return null;
    // utm_source left blank on purpose (e.g. ?utm_source=&utm_campaign=diwalifest)
    // — campaign/term are explicit and real, but source should come from the
    // actual click rather than a hardcoded value. Read it off document.referrer
    // rather than guessing; stays blank if the browser didn't send one.
    if (!source && (campaign || term)) {
      source = detectSourceFromReferrer() || '';
    }
    return { source, campaign, term };
  }

  const segs = (pathname || '').split('/').filter(Boolean);
  if (segs.length === 3 && !RESERVED_FIRST_SEGMENTS.has(segs[0].toLowerCase())) {
    return { source: sanitize(segs[0]), campaign: sanitize(segs[1]), term: sanitize(segs[2]) };
  }

  const referredSource = detectSourceFromReferrer();
  if (referredSource) return { source: referredSource, campaign: null, term: null };

  return null;
};

/**
 * First-touch write: only stores if nothing is stored yet, or the existing
 * record is older than the attribution window. Never overwrites a fresh
 * first touch with a later campaign click.
 */
export const captureUtmFirstTouch = (utm) => {
  if (!utm || (!utm.source && !utm.campaign && !utm.term)) return;
  try {
    const existingRaw = localStorage.getItem(STORAGE_KEY);
    if (existingRaw) {
      const existing = JSON.parse(existingRaw);
      const ageMs = Date.now() - (existing.capturedAt || 0);
      if (ageMs < ATTRIBUTION_WINDOW_MS) return; // still fresh — keep first touch
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...utm, capturedAt: Date.now() }));
  } catch (_) { /* private browsing / storage disabled — never break the page */ }
};

/** Read back the currently-attributed UTM triplet, expiry-checked. */
export const getTrackedUtm = () => {
  const empty = { source: null, campaign: null, term: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    const ageMs = Date.now() - (parsed.capturedAt || 0);
    if (ageMs >= ATTRIBUTION_WINDOW_MS) return empty;
    return { source: parsed.source || null, campaign: parsed.campaign || null, term: parsed.term || null };
  } catch (_) {
    return empty;
  }
};
